import React, { useState } from "react";
import Papa from "papaparse";
import { toast } from "react-toastify";
import { X } from "lucide-react";

const BulkUploadTimeLogs = ({
  isOpen,
  onClose,
  onBulkUpload,
  opportunities,
}) => {
  const [file, setFile] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (!file) {
      toast.error("Please select a CSV file.");
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const formattedData = results.data.map((timeLog) => {
          // Validate time format
          const start = new Date(`2000-01-01T${timeLog.startTime}`);
          const end = new Date(`2000-01-01T${timeLog.endTime}`);

          if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            throw new Error(
              `Invalid time format for entry with studentId: ${timeLog.studentId}`
            );
          }

          // Calculate total hours
          const diffInMs = end - start;
          const totalHours = diffInMs / (1000 * 60 * 60);

          if (totalHours <= 0 || totalHours > 24) {
            throw new Error(
              `Invalid time range for entry with studentId: ${timeLog.studentId}`
            );
          }

          // Validate status
          const validStatuses = ["pending", "approved", "rejected"];
          const status = timeLog.status?.toLowerCase() || "pending";

          if (!validStatuses.includes(status)) {
            throw new Error(
              `Invalid status "${timeLog.status}" for entry with studentId: ${
                timeLog.studentId
              }. Must be one of: ${validStatuses.join(", ")}`
            );
          }

          return {
            id: Date.now() + Math.random(),
            studentId: timeLog.studentId,
            opportunityId: parseInt(timeLog.opportunityId),
            date: timeLog.date,
            startTime: timeLog.startTime,
            endTime: timeLog.endTime,
            description: timeLog.description,
            status: status,
          };
        });

        onBulkUpload(formattedData);
        toast.success("Time logs uploaded successfully!");
        setFile(null);
        onClose();
      },
      error: function (error) {
        console.error(error);
        toast.error("Error parsing CSV file.");
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>
        {/* Modal Title */}
        <h2 id="modal-title" className="text-xl font-bold mb-4">
          Bulk Upload Time Logs
        </h2>
        {/* File Input */}
        <div className="space-y-4">
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="w-full"
          />
          <p className="text-sm text-gray-500">
            <span className="mb-1 block">
              Please upload a CSV file with the following headers:
            </span>
            <code className="text-xs bg-gray-100 p-1 rounded">
              studentId, opportunityId, date, startTime, endTime, description,
              status
            </code>
          </p>
          <p className="text-xs text-gray-400">
            Download our{" "}
            <a
              href="/sample_time_logs.csv"
              download
              className="text-blue-500 underline hover:text-blue-600"
            >
              sample file
            </a>{" "}
            for reference.
          </p>
          <p className="text-xs text-gray-400">
            Note: date should be in YYYY-MM-DD format, times should be in
            24-hour format (HH:MM), status should be one of: pending, approved,
            rejected
          </p>
        </div>
        {/* Form Buttons */}
        <div className="flex justify-end space-x-2 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadTimeLogs;
